import { Typography } from '@mui/material';
import { createPlanPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import type { CreditPurchaseConfig, Currency, FixedProductInput } from '@vy/types';
import { isEmptyArray, isNotEmptyArray, withDefault } from '@vy/utils';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { PLAN_CREATION_REASONS } from '../../../../../../../contexts/create-plan-context/create-plan-context-types/plan-create-reason';
import { useCreatePlanContext } from '../../../../../../../contexts/create-plan-context';
import { useDialog } from '../../../../../../../hooks/use-dialog';
import { useGetDefaultProductConfigurations } from '../../../../create-plan-page-hooks/useGetDefaultProductConfigurations';
import { usePartitionedFixedProducts } from '../../../../create-plan-page-hooks/usePartitionedFixedProducts';
import type { NewPlanForm } from '../../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { AddCreditDrawer } from '../../../AddCreditDrawer/AddCreditDrawer';
import { buildCreditPurchaseConfig } from '../../../AddCreditDrawer/build-credit-purchase-item';
import type { AddCreditForm } from '../../../AddCreditDrawer/add-credit-form/add-credit-form-schema';
import { FixedChargesEditTable } from '../../../FixedChargesEditTable/FixedChargesEditTable';
import { CreditsSection } from '../../../CreditsEditTable/CreditsSection';
import { fromCredit, fromFixedProduct } from '../../../CreditsEditTable/credit-grant-view';
import type { CreditGrantView } from '../../../CreditsEditTable/credit-grant-view';
import { AddItemMenuButton } from '../../create-plan-page-tabs-components/AddItemMenuButton';
import { EmptyTab } from '../../create-plan-page-tabs-components/EmptyTab';
import { FixedChargesMenuItems } from './fixed-charges-tab-content-components/FixedChargesMenuItems';

type FixedChargesTabContentProps = {
  fixedProducts: FixedProductInput[];
  currency: Currency;
  isParentAgreement?: boolean;
};

export const FixedChargesTabContent: React.FC<FixedChargesTabContentProps> = ({ isParentAgreement }) => {
  const { control, setValue, getValues } = useFormContext<PlanInputForm, NewPlanForm>();
  const { getSubscriptionProductConfiguration } = useGetDefaultProductConfigurations();
  const { planCreationReason } = useCreatePlanContext();
  const {
    isDialogOpen: isAddCreditDrawerOpen,
    handleOpen: handleOpenAddCredit,
    handleClose: handleCloseAddCredit,
  } = useDialog();

  const isNewContract = planCreationReason === PLAN_CREATION_REASONS.NewContract;
  const canManageCredits = isNewContract || planCreationReason === PLAN_CREATION_REASONS.EditContract;

  const { serviceFees, creditPurchasesFromProducts } = usePartitionedFixedProducts();

  const credits = (useWatch({ control, name: 'credits' }) ?? []) as CreditPurchaseConfig[];

  const creditRows: CreditGrantView[] = [
    ...credits.map((c, i) => fromCredit(c, i)),
    ...creditPurchasesFromProducts.map(({ product, originalIndex }) =>
      fromFixedProduct(product, originalIndex),
    ),
  ];
  const hasAnyCredits = creditRows.length > 0;

  const creditsReadOnly = !canManageCredits;

  const handleAddCredit = (data: AddCreditForm) => {
    const current = withDefault(getValues('credits'), []);
    setValue('credits', [...current, buildCreditPurchaseConfig(data)], { shouldDirty: true });
  };

  const onAddCreditClick = canManageCredits ? handleOpenAddCredit : undefined;

  useEffect(() => {
    if (isParentAgreement && isEmptyArray(serviceFees)) {
      const subscriptionProduct = getSubscriptionProductConfiguration();

      setValue('fixedProducts', [subscriptionProduct]);
    }
  }, [isParentAgreement, serviceFees, getSubscriptionProductConfiguration]);

  if (isEmptyArray(serviceFees) && !hasAnyCredits && !isParentAgreement) {
    return (
      <div className="flex w-full flex-col gap-y-4">
        <EmptyTab
          title="Create and Add Fixed Fees"
          description="Set up one-time fees, subscription charges or price per unit to tailor your pricing model."
        >
          <AddItemMenuButton
            cta={
              <span className="flex items-center gap-x-2">
                <VyIcon
                  icon="PlusMedium"
                  style={{
                    transform: 'scale(0.8)',
                  }}
                />
                <span>Add Fees</span>
              </span>
            }
            {...getSelectorAttribute(createPlanPageSelectors.tabs.sections.fixedCharges.addFeeButton)}
          >
            <FixedChargesMenuItems onAddCreditClick={onAddCreditClick} />
          </AddItemMenuButton>
        </EmptyTab>

        {isAddCreditDrawerOpen && (
          <AddCreditDrawer
            open={isAddCreditDrawerOpen}
            onClose={handleCloseAddCredit}
            onAddCredit={handleAddCredit}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-y-4">
      {!isParentAgreement && (
        <div className="flex items-center justify-end">
          <AddItemMenuButton
            color="secondary"
            cta={
              <span className="flex items-center gap-x-2">
                <VyIcon
                  icon="PlusMedium"
                  style={{
                    transform: 'scale(0.8)',
                  }}
                />
                <span>Add Item</span>
              </span>
            }
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            {...getSelectorAttribute(createPlanPageSelectors.tabs.sections.fixedCharges.addFeeButton)}
          >
            <FixedChargesMenuItems onAddCreditClick={onAddCreditClick} />
          </AddItemMenuButton>
        </div>
      )}

      {isNotEmptyArray(serviceFees) && (
        <>
          <Typography className="text-base font-medium">Service Fees</Typography>

          <FixedChargesEditTable fixedProducts={serviceFees} isParentAgreement={isParentAgreement} />
        </>
      )}

      {hasAnyCredits && <CreditsSection rows={creditRows} readOnly={creditsReadOnly} />}

      {isAddCreditDrawerOpen && (
        <AddCreditDrawer
          open={isAddCreditDrawerOpen}
          onClose={handleCloseAddCredit}
          onAddCredit={handleAddCredit}
        />
      )}
    </div>
  );
};
